import { useQueryStates } from 'nuqs'

import { USE_PAGINATION_DEFAULTS } from 'components/Table/Pagination/constants'
import { parseAsFilters, parseAsPositiveInteger } from './parser'

import type { FilterValue } from 'components/Filter'
import type { UsePagination } from 'components/Table/Pagination/types'
import type { Dispatch, SetStateAction } from 'react'

type Result = Pick<UsePagination, 'page' | 'rowsPerPage' | 'setPage' | 'setRowsPerPage'> & {
  filters: FilterValue[]
  setFilters: Dispatch<SetStateAction<FilterValue[]>>
}

export const useTableSearchParams = (): Result => {
  const [{ filters, page, rowsPerPage }, setSearchParams] = useQueryStates(
    {
      filters: parseAsFilters.withDefault([]),
      page: parseAsPositiveInteger.withDefault(USE_PAGINATION_DEFAULTS.FIRST_PAGE),
      rowsPerPage: parseAsPositiveInteger.withDefault(USE_PAGINATION_DEFAULTS.ROWS_PER_PAGE),
    },
    {
      urlKeys: {
        rowsPerPage: 'rows-per-page',
      },
    },
  )

  const setFilters: Dispatch<SetStateAction<FilterValue[]>> = nextFilters => {
    void setSearchParams(previous => ({
      filters: typeof nextFilters === 'function' ? nextFilters(previous.filters) : nextFilters,
      // Reset pagination because the selected page may not exist for the new filters
      page: 1,
    }))
  }

  const setPage: Dispatch<SetStateAction<number>> = nextPage => {
    void setSearchParams(previous => ({
      page: typeof nextPage === 'function' ? nextPage(previous.page) : nextPage,
    }))
  }

  const setRowsPerPage: Dispatch<SetStateAction<number>> = nextRowsPerPage => {
    void setSearchParams(previous => ({
      rowsPerPage:
        typeof nextRowsPerPage === 'function'
          ? nextRowsPerPage(previous.rowsPerPage)
          : nextRowsPerPage,
    }))
  }

  return {
    filters,
    page,
    rowsPerPage,
    setFilters,
    setPage,
    setRowsPerPage,
  }
}
